"use client";

import type { JSX } from "react";
import { Icon, type IconName } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";

interface SharePlatform {
  platform: IconName;
  label: string;
  build: (url: string, title: string) => string;
}

const PLATFORMS: SharePlatform[] = [
  {
    platform: "facebook",
    label: "Facebook",
    build: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    platform: "x",
    label: "X",
    build: (url, title) =>
      `https://x.com/intent/post?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    platform: "whatsapp",
    label: "WhatsApp",
    build: (url, title) =>
      `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    platform: "linkedin",
    label: "LinkedIn",
    build: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

/**
 * Social share icons that share the current page URL. The URL/title are read
 * from `window.location` at click time, so they resolve correctly under any
 * locale/route with no server-side URL plumbing or hydration concerns.
 */
export function EventShareLinks({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <div className={cn("flex items-center gap-4 text-text-primary", className)}>
      {PLATFORMS.map(({ platform, label, build }) => (
        <button
          key={platform}
          type="button"
          aria-label={`Share on ${label}`}
          onClick={() =>
            window.open(
              build(window.location.href, document.title),
              "_blank",
              "noopener,noreferrer",
            )
          }
          className="cursor-pointer transition-opacity hover:opacity-70"
        >
          <Icon name={platform} className="size-5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
